/**
 * navigation/platformAuthRedirect.ts
 * ──────────────────────────────────────────────────────────────────────────
 * SK Catalog does NOT own authentication. The SK Platform owns the only
 * login / signup / forgot-password screens for every product. When this app
 * has no valid session, we bounce the user to the centralized platform auth,
 * carrying the product handoff:
 *     ?product=catalog&return_to=<where they were>&state=<nonce>
 * and a short-lived `sk_auth_state` cookie (parent-domain scoped) the platform
 * verifies (CSRF / first-party-initiation). After auth, the platform redirects
 * back here, logged in via the shared session cookie.
 *
 * Web-only; no-op on native.
 */
import { Platform } from 'react-native';

// SK Platform auth entry point, auto-selected by hostname so the same build
// works in every environment without a build-arg:
//   localhost      → local platform dev port
//   *.dev.*        → platform dev
//   *.staging.*    → platform staging
//   anything else  → platform prod
function resolvePlatformAuthUrl(): string {
  if (typeof window === 'undefined') return 'https://platform.softknack.com/';
  const h = window.location.hostname;
  if (/^(localhost|127\.0\.0\.1)$/.test(h)) return 'http://localhost:3009/';
  if (h.includes('.dev.')) return 'https://platform.dev.softknack.com/';
  if (h.includes('.staging.')) return 'https://platform.staging.softknack.com/';
  return 'https://platform.softknack.com/';
}
const PLATFORM_AUTH_URL = resolvePlatformAuthUrl();
const PRODUCT = 'catalog';
const STATE_COOKIE = 'sk_auth_state';

const isWeb = (): boolean => Platform.OS === 'web' && typeof window !== 'undefined';

function randomState(): string {
  const bytes = new Uint8Array(16);
  (window.crypto as Crypto).getRandomValues(bytes);
  let hex = '';
  for (let i = 0; i < bytes.length; i += 1) hex += bytes[i].toString(16).padStart(2, '0');
  return hex;
}

function setStateCookie(state: string): void {
  const parts = window.location.hostname.split('.');
  // Parent-domain scope on real hosts (.softknack.com); host-only on localhost
  // (cookies ignore port, so it's still shared with the platform on another port).
  const domain = parts.length >= 2 && parts[parts.length - 1] !== 'localhost'
    ? `; domain=.${parts.slice(-2).join('.')}`
    : '';
  const secure = window.location.protocol === 'https:' ? '; Secure' : '';
  document.cookie = `${STATE_COOKIE}=${state}; Max-Age=600; path=/; SameSite=Lax${domain}${secure}`;
}

// Guard against firing twice within one page load (e.g. boot probe + a parallel
// 401) — the first call navigates away.
let _redirecting = false;

/** Bounce to the centralized platform auth, returning here after sign-in. */
export function redirectToPlatformAuth(): void {
  if (!isWeb() || _redirecting) return;
  _redirecting = true;

  const state = randomState();
  setStateCookie(state);

  const url = new URL(PLATFORM_AUTH_URL);
  url.searchParams.set('product', PRODUCT);
  url.searchParams.set('return_to', window.location.href);
  url.searchParams.set('state', state);
  window.location.assign(url.toString());
}

/**
 * Hand a verification token to the centralized platform verify screen
 * (`?mode=verify&token=…`). The SK Platform owns the ONLY verify /
 * "link expired" screen for every product — so a re-clicked or expired catalog
 * link is re-processed there and shows the platform's screen, never a local one.
 * Web-only; no-op on native.
 */
export function redirectToPlatformVerify(token: string): void {
  if (!isWeb() || _redirecting) return;
  _redirecting = true;

  const url = new URL(PLATFORM_AUTH_URL);
  url.searchParams.set('mode', 'verify');
  url.searchParams.set('token', token);
  window.location.assign(url.toString());
}

/**
 * Read (and clear) a `?mode=verify&token=…` from the URL — the catalog
 * verification email links back here. Returns the token once, then strips the
 * query so a reload / return-trip doesn't re-process a consumed token.
 * Web-only; returns null on native or when absent.
 */
export function takeVerifyToken(): string | null {
  if (!isWeb()) return null;
  const params = new URLSearchParams(window.location.search);
  if (params.get('mode') !== 'verify') return null;
  const token = params.get('token');
  if (!token) return null;
  window.history.replaceState({}, '', window.location.pathname);
  return token;
}

/**
 * Hand a password-reset token to the centralized platform reset screen
 * (`?mode=reset&token=…`). The SK Platform owns the ONLY reset-password /
 * "link expired" screen for every product — so a catalog reset link is
 * re-processed there and shows the platform's screen, never a local one and
 * never a bounce to plain login:
 *   • valid link    → platform's "Reset your password" form
 *   • used/expired  → platform's "link invalid / expired" screen
 * Web-only; no-op on native.
 */
export function redirectToPlatformReset(token: string): void {
  if (!isWeb() || _redirecting) return;
  _redirecting = true;

  const url = new URL(PLATFORM_AUTH_URL);
  url.searchParams.set('mode', 'reset');
  url.searchParams.set('token', token);
  window.location.assign(url.toString());
}

/**
 * Read (and clear) a `?mode=reset&token=…` from the URL — the catalog
 * password-reset email links back here. Returns the token once, then strips the
 * query so a reload doesn't re-process it. Web-only; returns null on native/absent.
 */
export function takeResetToken(): string | null {
  if (!isWeb()) return null;
  const params = new URLSearchParams(window.location.search);
  if (params.get('mode') !== 'reset') return null;
  const token = params.get('token');
  if (!token) return null;
  window.history.replaceState({}, '', window.location.pathname);
  return token;
}
